import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { usePricing } from "../context/PricingContext";
import { supabase } from "../supabaseClient";
import { AnalyticsManager, trackEvent } from "../utils/analytics";
import { startPayuCheckout } from "../utils/payuBolt";
import { runJuspayTopup, JuspaySkip } from "../utils/juspayCheckout";
import MembershipCard from "./MembershipCard";
import "./SubscriptionSection.css";

import { FaRegListAlt, FaUser, FaUserFriends, FaWallet } from "react-icons/fa";
import {
  RiChat3Line,
  RiPhoneFill,
  RiStackFill,
  RiTeamFill,
} from "react-icons/ri";

export interface PlanWithPrice {
  id: string;
  name: string;
  description: string;
  interval: "month" | "year";
  price: {
    id: number | string;
    amount: number;
    currency: string;
    originalAmount?: number;
    wallet_credit_amount?: number;
  };
}

interface SubscriptionSectionProps {
  plans: PlanWithPrice[];
  onSubscribe: (priceId: number | string) => void;
  isSubscribing: boolean;
  activePlanId: number | null;
  isLoading: boolean;
  error: string | null;
  currentUserPlanId: string | null;
}

// --- Icons ---
const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    preserveAspectRatio="xMidYMid meet"
  >
    <circle cx="12" cy="12" r="11.5" fill="#3BC97C" />
    <path
      d="M7.75 12.0001L10.58 14.8301L16.25 9.1701"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const PlanIconMonthly = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
    <path
      d="M17 12H12V17H17V12ZM16 1V3H8V1H6V3H5C3.89 3 3 3.89 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.89 20.1 3 19 3H18V1H16ZM19 19H5V8H19V19Z"
      fill="white"
    />
  </svg>
);
const PlanIconYearly = () => (
  <svg width="50" height="50" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z"
      fill="white"
    />
  </svg>
);
const PlanIconFree = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="white" />
    <path d="M2 17L12 22L22 17V12L12 17L2 12V17Z" fill="white" />
  </svg>
);

const SubscriptionSection = React.forwardRef<
  HTMLElement,
  SubscriptionSectionProps
>(
  (
    {
      plans,
      onSubscribe,
      isSubscribing,
      activePlanId,
      isLoading,
      error,
      currentUserPlanId,
    },
    ref,
  ) => {
    const { currency, refreshUserStatus, user, pricingVariant, userProfiles } =
      useAuth();
    const {
      packages,
      firstRechargeOffer,
      formatPrice,
      isLoading: isPricingLoading,
      prices,
      showSubscriptions,
      variant,
    } = usePricing();
    const [isMobile, setIsMobile] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<"free" | "month" | "year">(
      "month",
    );
    const [isPurchasing, setIsPurchasing] = useState<string | null>(null);

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 768);
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    // --- NEW: TAX STATE ---
    const [confirmingPackage, setConfirmingPackage] = useState<{
      id: string;
      amount: number;
      price: number;
    } | null>(null);

    // Track GST Summary View
    useEffect(() => {
      if (confirmingPackage && currency === "INR") {
        const basePrice = confirmingPackage.price;
        const gstAmount = Number((basePrice * 0.18).toFixed(2));
        const totalPayable = Number((basePrice + gstAmount).toFixed(2));

        trackEvent("gst_summary_viewed", {
          package_id: confirmingPackage.id,
          base_amount: basePrice,
          gst_amount: gstAmount,
          total_payable: totalPayable,
          currency: "INR",
        });
      }
    }, [confirmingPackage, currency]);

    // --- RECHARGE LOGIC ---

    // Razorpay's checkout.js is normally on the page, but NOT guaranteed: a slow
    // mobile network, a blocked first load, or an iOS back/forward-cache restore
    // can leave window.Razorpay undefined — and constructing it then THROWS,
    // which surfaced to users as the "Failed to initiate recharge" alert (real
    // incident: iOS Safari, 2026-07-16). Make the fallback self-sufficient:
    // inject the script on demand and wait for it.
    const ensureRazorpay = (timeoutMs = 8000): Promise<void> =>
      new Promise((resolve, reject) => {
        if ((window as any).Razorpay) return resolve();
        const SRC = "https://checkout.razorpay.com/v1/checkout.js";
        const t = window.setTimeout(
          () => reject(new Error("razorpay_script_timeout")),
          timeoutMs,
        );
        const done = () => {
          window.clearTimeout(t);
          resolve();
        };
        let s = document.querySelector(
          `script[src="${SRC}"]`,
        ) as HTMLScriptElement | null;
        if (!s) {
          s = document.createElement("script");
          s.src = SRC;
          s.async = true;
          document.body.appendChild(s);
        } else if ((window as any).Razorpay) {
          return done();
        }
        s.addEventListener("load", done);
        s.addEventListener("error", () => {
          window.clearTimeout(t);
          reject(new Error("razorpay_script_failed"));
        });
      });

    // PayU (India) hosted checkout. RETIRED 2026-10: Juspay took its slot in
    // executeRecharge below. Kept intact, not deleted, so it can be revived by
    // restoring the one call there. Its server side is parked too.
    //
    // Original note: redirects the browser to PayU via an auto-submitted form;
    // the payu-callback edge fn verifies + credits + redirects back. Throws so
    // the caller can fall back to Razorpay if PayU can't start
    // (create-payu-payment errors or is 'skip'ped by the server-side toggle).
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const executePayuRecharge = async (
      pkg: { id: string; amount: number; price: number },
      finalAmountToPay: number,
      finalCreditAmount: number,
    ) => {
      // PayU has international acceptance enabled, so all currencies route through
      // it (INR + foreign). Falls back to Razorpay on any error.
      const { data, error } = await supabase.functions.invoke("create-payu-payment", {
        body: {
          pay_amount: finalAmountToPay,
          credit_amount: finalCreditAmount,
          currency: (currency || "INR").toUpperCase(),
          purpose: "wallet_topup",
          package_id: pkg.id,
          return_origin: window.location.origin,
        },
      });
      if (error) throw new Error(error.message || "payu_init_failed");
      if (!data || (data as any).error || (data as any).skip) {
        throw new Error((data as any)?.reason || (data as any)?.error || "payu_skip");
      }
      if (!(data as any).payu_url || !(data as any).params) throw new Error("payu_bad_response");

      trackEvent("Quick Recharge Started", {
        amount_credit: finalCreditAmount,
        amount_pay: finalAmountToPay,
        currency: (currency || "INR").toUpperCase(),
        variant,
        gateway: "payu",
      });

      // Close our own Payment Summary modal so PayU's in-page popup isn't hidden
      // behind it (the redirect flow used to navigate away, which closed it).
      setConfirmingPackage(null);

      // In-page PayU popup (Bolt) with automatic redirect fallback if it can't
      // render — so it never hangs. Settlement stays server-side (webhook / surl).
      await startPayuCheckout(data as any, (outcome) => {
        if (outcome === "success") {
          // Fire the same purchase pixels the Razorpay handler fires (Meta
          // Purchase, CleverTap Charged, Amplitude) — PayU settles server-side,
          // so this popup callback is the only client moment that knows the txn.
          AnalyticsManager.logPurchase(
            String((data as any).params?.txnid || ""),
            finalAmountToPay,
            (currency || "INR").toUpperCase(),
            [
              {
                itemId: String(pkg.id),
                itemName: `Wallet Recharge - ${formatPrice(finalCreditAmount)}`,
                price: finalAmountToPay,
                quantity: 1,
              },
            ],
          );
          window.location.assign("/payment-success?type=topup");
        } else {
          setIsPurchasing(null); // popup cancelled — re-enable the button
        }
      });
    };

    // Juspay HyperCheckout (Cashfree behind it), in an in-page iframe. See
    // utils/juspayCheckout.ts. The server prices the pack; this only says which
    // pack, and whether this screen showed GST (the INR summary below does).
    //
    // Throws JuspaySkip when Juspay is not available for this top-up (rollout
    // switch off, currency not opened yet, could not start), and ONLY then, so
    // executeRecharge falls back to Razorpay exactly as it did for PayU. Once the
    // checkout has opened, every outcome is handled here.
    const executeJuspayRecharge = async (
      pkg: { id: string; amount: number; price: number },
      finalAmountToPay: number,
      finalCreditAmount: number,
    ) => {
      const cur = (currency || "INR").toUpperCase();
      const withGst = cur === "INR" && finalAmountToPay > pkg.price;

      const r = await runJuspayTopup(
        { packageId: String(pkg.id), currency: cur, withGst },
        {
          // Close our Payment Summary only once the checkout is actually on
          // screen; on a skip it stays put for the Razorpay fallback.
          onOpened: () => setConfirmingPackage(null),
          // Same pixels and events the Razorpay handler fires (Meta Purchase,
          // CleverTap Charged, Amplitude), so a Juspay sale is counted the
          // same. runJuspayTopup fires them exactly once, including when the
          // payment confirms after this screen has stopped waiting.
          purchase: {
            userId: user?.id || "",
            value: finalAmountToPay,
            items: [
              {
                itemId: String(pkg.id),
                itemName: `Wallet Recharge - ${formatPrice(finalCreditAmount)}`,
                price: finalAmountToPay,
                quantity: 1,
              },
            ],
          },
        },
      );

      if (r.result === "credited") {
        trackEvent("Quick Recharge Succeeded", {
          package_id: pkg.id,
          amount_credit: finalCreditAmount,
          amount_pay: finalAmountToPay,
          currency: cur,
          juspay_order_id: r.orderId,
          user_id: user?.id,
          source: "Subscription Section",
          variant: variant,
          gateway: "juspay",
        });
        window.dispatchEvent(new CustomEvent("aura:payment-success"));
        setTimeout(async () => {
          await refreshUserStatus();
          setIsPurchasing(null);
        }, 1500);
        return;
      }

      setIsPurchasing(null);
      if (r.result === "processing") {
        // Possibly paid (a UPI app opened, a bank page) and not confirmed. The
        // webhook and the sweep credit it if money moved; never tell this
        // person it failed, and never that it was received when it may not be.
        alert(
          "Payment not confirmed yet. If money left your account, your wallet is credited " +
            "automatically within about 10 minutes. If you didn't pay, nothing was charged.",
        );
        refreshUserStatus().catch(() => {});
      } else if (r.result === "not_paid") {
        alert("Payment not completed. Nothing was charged.");
      } else if (r.result === "failed") {
        trackEvent("Recharge Failed", { gateway: "juspay", juspay_order_id: r.orderId });
        alert("The payment didn't go through. Please try again.");
      }
      // cancelled: they closed it without paying. Nothing to say.
    };

    // Entry point: Juspay first, Razorpay if Juspay is not available.
    const executeRecharge = async (
      pkg: { id: string; amount: number; price: number },
      finalAmountToPay: number,
      finalCreditAmount: number,
    ) => {
      setIsPurchasing(String(pkg.id));
      // A page restored from iOS Safari's back/forward cache (e.g. after
      // bouncing off a gateway error page) can carry an EXPIRED access token —
      // both payment functions then 401 before doing anything and the user just
      // sees a generic failure. getSession() refreshes the token when needed.
      try {
        await supabase.auth.getSession();
      } catch {
        /* the invokes below will surface any real auth problem */
      }
      try {
        await executeJuspayRecharge(pkg, finalAmountToPay, finalCreditAmount);
        return; // Juspay handled it, whatever the outcome
      } catch (e) {
        if (!(e instanceof JuspaySkip)) {
          // Not a skip: something broke after the checkout opened, which
          // runJuspayTopup is built never to do. Do NOT open a second checkout
          // on top of a payment that may have gone through.
          console.error("[recharge] Juspay error after opening:", e);
          setIsPurchasing(null);
          return;
        }
        console.warn("[recharge] Juspay unavailable, falling back to Razorpay:", e.reason);
      }
      // PayU used to be tried here. Retired 2026-10; to revive, restore:
      //   try { await executePayuRecharge(pkg, finalAmountToPay, finalCreditAmount); return; } catch {}
      await executeRazorpayRecharge(pkg, finalAmountToPay, finalCreditAmount);
    };

    // Razorpay flow (unchanged) — now the fallback path.
    const executeRazorpayRecharge = async (
      pkg: { id: string; amount: number; price: number },
      finalAmountToPay: number,
      finalCreditAmount: number,
    ) => {
      try {
        const currencyToLog = currency || "USD";

        trackEvent("Quick Recharge Started", {
          amount_credit: finalCreditAmount,
          amount_pay: finalAmountToPay,
          currency: currencyToLog,
          variant: variant,
        });

        setIsPurchasing(String(pkg.id)); // Track by ID to prevent multi-click

        const { data, error } = await supabase.functions.invoke(
          "create-topup-session",
          {
            body: {
              amount: finalAmountToPay, // Gateway charges this
              credit_amount: finalCreditAmount, // DB adds this to wallet
              currency: currencyToLog,
              package_id: pkg.id, // Good practice to pass ID
            },
          },
        );

        if (error)
          throw new Error(error.message || "Failed to create checkout session");

        if (data?.gateway === "razorpay" && data?.order) {
          // The order exists server-side; make sure the checkout SDK exists
          // client-side before constructing it (see ensureRazorpay above).
          await ensureRazorpay();
          const options = {
            key: data.key_id,
            amount: data.order.amount,
            currency: data.order.currency,
            name: "Aura AI Wallet Recharge",
            description: `Add ${formatPrice(finalCreditAmount)}`,
            order_id: data.order.id,
            handler: async function (response: any) {
              AnalyticsManager.logPurchase(
                data.order.id,
                finalAmountToPay, // Use the final payable amount (including GST if applicable)
                currencyToLog,
                [
                  {
                    itemId: String(pkg.id),
                    itemName: `Wallet Recharge - ${formatPrice(finalCreditAmount)}`,
                    price: finalAmountToPay,
                    quantity: 1,
                  },
                ],
              );

              trackEvent("Quick Recharge Succeeded", {
                package_id: pkg.id,
                amount_credit: finalCreditAmount,
                amount_pay: finalAmountToPay,
                currency: currencyToLog,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                user_id: user?.id,
                source: "Subscription Section",
                variant: variant,
              });

              // Clear confirmation state
              setConfirmingPackage(null);

              // Trigger the post-payment refer & earn upsell (app parity).
              window.dispatchEvent(new CustomEvent("aura:payment-success"));

              // Optimistically update Context
              setTimeout(async () => {
                await refreshUserStatus();
                setIsPurchasing(null);
              }, 1500);
            },
            modal: {
              ondismiss: function () {
                trackEvent("payment_modal_dismissed", { package_id: pkg.id });
                setIsPurchasing(null);
              },
            },
            theme: { color: "#ed0687" },
            prefill: {
              name:
                userProfiles?.[0]?.name || user?.user_metadata?.full_name || "",
              email: user?.email || "",
              contact: user?.phone || "",
            },
          };
          const razorpay = new (window as any).Razorpay(options);
          razorpay.open();
        } else if (data?.url) {
          window.location.href = data.url;
        }
      } catch (err: any) {
        trackEvent("Recharge Failed", {
          error_message: err.message,
        });
        console.error("[SubscriptionSection] Recharge Error:", err);
        alert("Failed to initiate recharge. Please try again.");
        setIsPurchasing(null);
      }
    };

    // 1. Initial Click Handler
    const handleQuickRecharge = async (pkg: {
      id: string;
      amount: number;
      price: number;
    }) => {
      // Intercept for Indian Users
      if (currency === "INR") {
        setConfirmingPackage(pkg);
        return;
      }

      // Standard Flow (USD etc) - No Tax / Inclusive
      // amount: Credit, price: Cost
      executeRecharge(pkg, pkg.price, pkg.amount);
    };

    // 2. Render Payment Summary Overlay
    const renderPaymentSummary = () => {
      if (!confirmingPackage) return null;

      const basePrice = confirmingPackage.price;
      const creditAmount = confirmingPackage.amount;
      // Calculate 18% GST with 2 decimal precision
      const gstAmount = Number((basePrice * 0.18).toFixed(2));
      const totalPayable = Number((basePrice + gstAmount).toFixed(2));

      // Bonus Calculation for display
      const hasBonus = creditAmount > basePrice;
      const bonusAmount = creditAmount - basePrice;
      const bonusPercent =
        basePrice > 0 ? Math.round((bonusAmount / basePrice) * 100) : 0;

      // Helper to force 2 decimals just for this summary
      const formatDecimalPrice = (val: number) => {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currency || "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(val / 100);
      };

      return (
        <div
          className="payment-summary-overlay"
          onClick={() => !isPurchasing && setConfirmingPackage(null)}
        >
          <div className="summary-modal" onClick={(e) => e.stopPropagation()}>
            <div className="summary-header">
              <h3 className="summary-title">Payment Summary</h3>
            </div>

            <div className="invoice-table">
              {/* Row 1: Base Price */}
              <div className="invoice-row">
                <span className="invoice-label">Recharge Amount</span>
                <span className="invoice-value">
                  {formatDecimalPrice(basePrice)}
                </span>
              </div>

              {/* Row 2: GST */}
              <div className="invoice-row">
                <span className="invoice-label">GST @ 18%</span>
                <span className="invoice-value">
                  {formatDecimalPrice(gstAmount)}
                </span>
              </div>

              {/* Row 3: Total Payable */}
              <div className="invoice-row total-row">
                <span className="invoice-total-label">Total Amount</span>
                <span className="invoice-total-value">
                  {formatDecimalPrice(totalPayable)}
                </span>
              </div>
            </div>

            {/* Benefit / Credit Info */}
            <div className="benefit-section">
              <div className="benefit-card">
                <div className="benefit-text">
                  <span className="benefit-label">
                    Total Credits
                    {hasBonus && (
                      <span className="extra-badge">
                        +{bonusPercent}% Extra
                      </span>
                    )}
                  </span>
                  <span className="benefit-sub">Added to your wallet</span>
                </div>
                <div className="benefit-value">{formatPrice(creditAmount)}</div>
              </div>
            </div>

            <div className="summary-actions">
              <button
                className="btn-cancel"
                onClick={() => setConfirmingPackage(null)}
                disabled={!!isPurchasing}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={() =>
                  executeRecharge(confirmingPackage, totalPayable, creditAmount)
                }
                disabled={!!isPurchasing}
              >
                {isPurchasing
                  ? "Processing..."
                  : `Pay ${formatDecimalPrice(totalPayable)}`}
              </button>
            </div>
          </div>
        </div>
      );
    };

    const renderRecharge = () => {
      if (isPricingLoading || packages.length === 0) return null;

      // Prepend the one-time first-recharge offer (never-paid users only) so it
      // sits first in the existing recharge grid — no separate/duplicate grid.
      const rechargeList = firstRechargeOffer
        ? [firstRechargeOffer, ...packages]
        : packages;

      return (
        <div className="recharge-section">
          <div className="pricing-header">
            <span className="pricing-tag">⚡ Quick Recharge Options</span>
            <p className="recharge-sub">Get bonus credits with larger packs.</p>
          </div>

          <div className="recharge-grid">
            {rechargeList.map((pkg) => {
              const isOffer = !!(pkg as any).is_first_recharge_offer;
              const hasBonus = pkg.amount > pkg.price;
              const bonusAmount = pkg.amount - pkg.price;
              const bonusPercent =
                pkg.price > 0 ? Math.round((bonusAmount / pkg.price) * 100) : 0;

              return (
                <button
                  key={pkg.id}
                  className="recharge-card"
                  style={{
                    borderColor: isOffer
                      ? "#ed068788"
                      : hasBonus
                        ? "#ffeb9923"
                        : "#9dffc18c", // PINK offer, GOLD bonus, GREEN otherwise
                    opacity: isPurchasing === String(pkg.id) ? 0.7 : 1,
                    position: "relative",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isPurchasing) handleQuickRecharge(pkg);
                  }}
                >
                  {/* ONE-TIME OFFER RIBBON */}
                  {isOffer && (
                    <div
                      className="offer-ribbon"
                      style={{
                        position: "absolute",
                        top: "-12px",
                        left: "-8px",
                        background: "linear-gradient(90deg, #ff8c42, #ed0687)",
                        color: "#fff",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.03em",
                        padding: "3px 10px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 6px rgba(237, 6, 135, 0.35)",
                      }}
                    >
                      ONE-TIME OFFER
                    </div>
                  )}

                  {/* BONUS BADGE */}
                  {hasBonus && (
                    <div
                      className="bonus-badge"
                      style={{
                        position: "absolute",
                        top: "-12px",
                        right: "-12px",
                        background:
                          "linear-gradient(90deg, #c65205 0%, #ffff07cb 100%)",
                        color: "#1f002e",
                        fontSize: "0.88rem",
                        fontWeight: "700",
                        padding: "3px 10px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 6px rgba(255, 65, 65, 0.25)",
                      }}
                    >
                      +{bonusPercent}% Extra
                    </div>
                  )}

                  <div className="recharge-content">
                    {/* PAY (larger text) */}
                    <p
                      className="recharge-amount"
                      style={{
                        fontSize: "1.60rem",
                        fontWeight: 800,
                        marginBottom: "4px",
                      }}
                    >
                      {formatPrice(pkg.price, { maximumFractionDigits: 0 })}
                    </p>

                    {/* GET (larger text) */}
                    {hasBonus && (
                      <p
                        className="recharge-meta"
                        style={{
                          fontSize: "1.20rem",
                          color: "#22c55e",
                          fontWeight: "600",
                        }}
                      >
                        Get{" "}
                        <strong>
                          {formatPrice(pkg.amount, {
                            maximumFractionDigits: 0,
                          })}
                        </strong>
                      </p>
                    )}

                    <p
                      className="recharge-status"
                      style={{
                        fontSize: "0.8rem",
                        marginTop: "4px",
                        opacity: 0.6,
                      }}
                    >
                      {isPurchasing === String(pkg.id) ? "Redirecting…" : ""}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* RENDER OVERLAY IF ACTIVE */}
          {renderPaymentSummary()}
        </div>
      );
    };

    // --- NEW HELPER: Calculate Effective Cost per Unit ---
    const getEffectiveRateDisplay = (
      planCost: number,
      creditAmount: number,
    ) => {
      const stdChatCost = prices["chat_message"] || 0;
      const stdCallCost = prices["voice_call_minute"] || 0;

      // Avoid division by zero
      if (creditAmount <= 0 || planCost <= 0) {
        // Fallback if data is missing
        return (
          <span>
            Chat <strong>{formatPrice(stdChatCost)}</strong> | Call{" "}
            <strong>{formatPrice(stdCallCost)}</strong>/min
          </span>
        );
      }

      // 1. Calculate Discount Ratio (e.g. Pay 299 / Get 450 = 0.66)
      const ratio = planCost / creditAmount;

      // 2. Apply ratio to standard costs
      const effChat = stdChatCost * ratio;
      const effCall = stdCallCost * ratio;

      return (
        <span>
          Chat <strong>{formatPrice(effChat)}</strong>/Question | Call{" "}
          <strong>{formatPrice(effCall)}</strong>/min
          <br />
          <span
            style={{
              fontSize: "0.8em",
              opacity: 0.75,
              fontWeight: "normal",
              display: "block",
              marginTop: "2px",
            }}
          >
            (Effective cost based on plan)
          </span>
        </span>
      );
    };

    const renderFree = () => {
      // 1. Get Welcome Bonus (Ensure 'welcome_bonus' exists in service_prices DB table)
      const welcomeBonus = prices["welcome_bonus"] || 0;

      // 2. Calculate "1 Free Chat" based on math
      const msgCost = prices["chat_message"] || 1;
      const freeChats = Math.floor(welcomeBonus / msgCost);
      const chatText =
        freeChats > 0 ? `${freeChats} Free Chat Included` : "Pay Per Chat";

      return (
        <div className="pricing-card free-plan">
          <div className="card-header">
            <div className="plan-icon">
              <PlanIconFree />
            </div>
            <h3 className="plan-title">Free Plan</h3>
            <p className="plan-description">
              Your free starting point into astrology.
            </p>
          </div>
          <div className="plan-price">
            <span className="price-main">{formatPrice(0)}</span>
            <span className="price-interval">/month</span>
          </div>
          <p className="plan-billing-info">Free forever</p>

          <ul className="plan-features new-features">
            <li className="feature-item">
              <FaWallet />{" "}
              <span>
                <strong>{formatPrice(welcomeBonus)}</strong> Welcome Bonus
              </span>
            </li>
            <li className="feature-item">
              <RiChat3Line /> <span>{chatText}</span>
            </li>
            <li className="feature-item">
              <FaRegListAlt /> <span>1 Basic Report For Free</span>
            </li>
            <li className="feature-item">
              <FaUser /> <span>1 User Profile</span>
            </li>
          </ul>
        </div>
      );
    };

    const renderMonthly = (plan: PlanWithPrice) => {
      const creditAmount = plan.price.wallet_credit_amount || 0;
      const planCost = plan.price.amount || 0;

      return (
        <div className="pricing-card">
          <div className="plan-value-badge monthly-badge">
            <FaWallet />{" "}
            <span>{formatPrice(creditAmount)} Credit Included</span>
          </div>
          <div className="card-header">
            <div className="plan-icon">
              <PlanIconMonthly />
            </div>
            <h3 className="plan-title">{plan.name}</h3>
            <p className="plan-description">
              Unlock deeper cosmic insights every month.
            </p>
          </div>
          <div className="plan-price">
            <span className="price-main">{formatPrice(planCost)}</span>
            <span className="price-interval">/month</span>
          </div>
          <p className="plan-billing-info">Billed monthly • Cancel anytime</p>

          <ul className="plan-features new-features">
            {/* 1. Wallet Credit */}
            <li className="feature-item major-feature">
              <FaWallet />{" "}
              <span>
                <strong>{formatPrice(creditAmount)}</strong> Monthly Wallet
                Credit
              </span>
            </li>

            {/* 2. Effective Rate (New Logic) */}
            <li className="feature-item" style={{ alignItems: "flex-start" }}>
              <RiChat3Line style={{ marginTop: "3px" }} />
              {getEffectiveRateDisplay(planCost, creditAmount)}
            </li>

            {/* 3. Reports */}
            <li className="feature-item">
              <FaRegListAlt /> <span>All Premium Reports For Free</span>
            </li>

            {/* 4. Profiles */}
            <li className="feature-item">
              <FaUserFriends /> <span>Manage 4 Profiles</span>
            </li>
          </ul>

          <button
            className="plan-button"
            onClick={() => {
              trackEvent("Subscription Clicked", {
                plan: plan.name,
                priceId: plan.price.id,
                interval: "monthly",
                cost: plan.price.amount,
                variant: variant,
              });
              onSubscribe(plan.price.id);
            }}
            disabled={
              (isSubscribing && activePlanId === Number(plan.price.id)) ||
              currentUserPlanId === "monthly" ||
              currentUserPlanId === "yearly"
            }
          >
            {isSubscribing && activePlanId === Number(plan.price.id)
              ? "Processing..."
              : currentUserPlanId === "monthly"
                ? "Current Plan"
                : "Begin Journey"}
          </button>
        </div>
      );
    };

    const renderYearly = (plan: PlanWithPrice) => {
      const creditAmount = plan.price.wallet_credit_amount || 0;
      const planCost = plan.price.amount || 0;

      return (
        <div className="pricing-card yearly-plan">
          <div className="plan-value-badge">
            <FaWallet /> <span>Best Value</span>
          </div>
          <div className="card-header">
            <div className="plan-icon">
              <PlanIconYearly />
            </div>
            <h3 className="plan-title">{plan.name}</h3>
            <p className="plan-description">{plan.description}</p>
          </div>
          <div className="plan-price">
            {plan.price.originalAmount && (
              <span className="price-strikethrough">
                {formatPrice(plan.price.originalAmount)}
              </span>
            )}
            <span className="price-main">{formatPrice(planCost)}</span>
            <span className="price-interval">/year</span>
          </div>
          <p className="plan-billing-info">Billed Yearly • Cancel anytime</p>

          <ul className="plan-features new-features">
            {/* 1. Wallet Credit */}
            <li className="feature-item major-feature">
              <RiStackFill />{" "}
              <span>
                <strong>{formatPrice(creditAmount)}</strong> Annual Wallet
                Credit
              </span>
            </li>

            {/* 2. Effective Rate (New Logic) */}
            <li className="feature-item" style={{ alignItems: "flex-start" }}>
              <RiChat3Line style={{ marginTop: "3px" }} />
              {getEffectiveRateDisplay(planCost, creditAmount)}
            </li>

            {/* 3. Reports */}
            <li className="feature-item">
              <FaRegListAlt /> <span>All Premium Reports For Free</span>
            </li>

            {/* 4. Profiles */}
            <li className="feature-item">
              <RiTeamFill /> <span>Manage 10 Profiles</span>
            </li>
          </ul>

          <button
            className="plan-button"
            onClick={() => {
              trackEvent("Subscription Clicked", {
                plan: plan.name,
                priceId: plan.price.id,
                interval: "yearly",
                cost: plan.price.amount,
                variant: variant,
              });
              onSubscribe(plan.price.id);
            }}
            disabled={
              (isSubscribing && activePlanId === Number(plan.price.id)) ||
              currentUserPlanId === "monthly" ||
              currentUserPlanId === "yearly"
            }
          >
            {isSubscribing && activePlanId === Number(plan.price.id)
              ? "Processing..."
              : currentUserPlanId === "yearly"
                ? "Current Plan"
                : "Unlock Destiny"}
          </button>
        </div>
      );
    };

    if (isLoading)
      return <div className="pricing-section">Loading plans...</div>;
    if (error && showSubscriptions)
      return <div className="pricing-section">Error: {error}</div>; // Only show plan error if we expect plans

    const monthlyPlan = plans.find((p) => p.interval === "month");
    const yearlyPlan = plans.find((p) => p.interval === "year");
    // const shouldShowSubscriptions = pricingVariant === 'control';

    return (
      <section className="pricing-section" id="pricing" ref={ref}>
        <div className="pricing-header">
          {/* "Pricing Plans" tag removed (redundant with the rates card). */}

          {!isPricingLoading &&
            (prices["voice_call_minute"] ||
              prices["chat_message"] ||
              prices["report_premium"]) && (
              /* One unified rates card with three divider-separated columns —
                 mirrors the app's wallet rates card (not three cramped pills). */
              <div className="rates-card">
                <div className="rate-cell">
                  <div className="rate-cell-head">
                    <RiChat3Line />
                    <span>Chat</span>
                  </div>
                  <strong className="rate-value">
                    {formatPrice(prices["chat_message"])}
                  </strong>
                  <span className="rate-unit">/question</span>
                </div>

                <div className="rate-divider" />

                <div className="rate-cell">
                  <div className="rate-cell-head">
                    <RiPhoneFill />
                    <span>Call</span>
                  </div>
                  <strong className="rate-value">
                    {formatPrice(prices["voice_call_minute"])}
                  </strong>
                  <span className="rate-unit">/min</span>
                </div>

                <div className="rate-divider" />

                <div className="rate-cell">
                  <div className="rate-cell-head">
                    <FaRegListAlt />
                    <span>Report</span>
                  </div>
                  <strong className="rate-value">
                    {formatPrice(prices["report_premium"])}
                  </strong>
                  <span className="rate-unit">/report</span>
                </div>
              </div>
            )}
        </div>

        {/* Aura Plus gold banner — placed right AFTER the per-service rates,
            matching the app's wallet layout (rates → membership → packages). */}
        <MembershipCard />

        {/* The Recharge section is now ALWAYS visible */}
        {renderRecharge()}

        {showSubscriptions &&
          (isMobile ? (
            <>
              <div className="plan-toggle">
                <button
                  className={`toggle-btn ${selectedPlan === "free" ? "active" : ""}`}
                  onClick={() => setSelectedPlan("free")}
                >
                  Free
                </button>
                <button
                  className={`toggle-btn ${selectedPlan === "month" ? "active" : ""}`}
                  onClick={() => setSelectedPlan("month")}
                >
                  Monthly
                </button>
                <button
                  className={`toggle-btn ${selectedPlan === "year" ? "active" : ""}`}
                  onClick={() => setSelectedPlan("year")}
                >
                  Yearly
                </button>
              </div>
              <div className="plans-grid">
                {selectedPlan === "free" && renderFree()}
                {selectedPlan === "month" &&
                  monthlyPlan &&
                  renderMonthly(monthlyPlan)}
                {selectedPlan === "year" &&
                  yearlyPlan &&
                  renderYearly(yearlyPlan)}
              </div>
            </>
          ) : (
            <div className="plans-grid">
              {renderFree()}
              {monthlyPlan && renderMonthly(monthlyPlan)}
              {yearlyPlan && renderYearly(yearlyPlan)}
            </div>
          ))}
      </section>
    );
  },
);

export default SubscriptionSection;
